import React, { FormEvent, useEffect, useRef, useState } from "react";
import {
  CONTACT_SETTINGS_API,
  ContactSettings,
  contactSettingsToLinks,
  defaultContactSettings,
  isContactSettings,
} from "../data/contactSettings";
import { useContactSettings } from "../data/ContactSettingsContext";
import "../styles/ContactControlPage.css";

interface ContactSettingsRecord {
  draft: ContactSettings;
  published: ContactSettings;
  draftVersion: number;
  publishedVersion: number;
  updatedAt: string | null;
  publishedAt: string | null;
}

interface SignInResponse {
  access_token: string;
  user?: { email?: string; name?: string };
}

class RequestError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = "RequestError";
  }
}

const previewMode =
  process.env.NODE_ENV === "development" &&
  new URLSearchParams(window.location.search).get("preview") === "1";

const copySettings = (settings: ContactSettings): ContactSettings => ({
  ...settings,
  socials: settings.socials.map((social) => ({ ...social })),
});

const settingsEqual = (first: ContactSettings, second: ContactSettings) =>
  JSON.stringify(first) === JSON.stringify(second);

const dateLabel = (value: string | null) => {
  if (!value) return "Not yet published";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Recently updated"
    : parsed.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

async function request<T>(
  path: string,
  token?: string,
  init: RequestInit = {}
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${CONTACT_SETTINGS_API}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new RequestError("The server could not be reached. Check your connection and try again.", 0);
  }

  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // A server error can legitimately return an empty body.
  }

  if (!response.ok) {
    const validationDetails =
      response.status === 422 && Array.isArray(body?.detail)
        ? body.detail
            .map((issue: any) => {
              const field = Array.isArray(issue?.loc)
                ? issue.loc.filter((part: unknown) => part !== "body").join(" → ")
                : "Field";
              return `${field}: ${typeof issue?.msg === "string" ? issue.msg : "invalid value"}`;
            })
            .slice(0, 3)
            .join("; ")
        : null;
    const serverMessage =
      typeof body?.detail === "string"
        ? body.detail
        : typeof body?.message === "string"
          ? body.message
          : null;
    const fallback =
      response.status === 401
        ? "Sign-in expired or was rejected. Please sign in again."
        : response.status === 403
          ? "This account does not have AlluraTech admin access."
        : response.status === 409
          ? "Another admin updated this page. Refresh the version while keeping your edits, then review before saving."
          : "The change could not be saved. Please try again.";
    throw new RequestError(validationDetails || serverMessage || fallback, response.status);
  }

  return body as T;
}

async function getSettings(token: string): Promise<ContactSettingsRecord> {
  const result = await request<ContactSettingsRecord>("/admin/contact-settings", token);
  if (
    !isContactSettings(result?.draft) ||
    !isContactSettings(result?.published) ||
    typeof result?.draftVersion !== "number" ||
    typeof result?.publishedVersion !== "number"
  ) {
    throw new RequestError("The server returned contact information in an unexpected format.", 0);
  }
  return result;
}

const ContactControlPage: React.FC = () => {
  const { reload: reloadPublicSettings } = useContactSettings();
  const [token, setToken] = useState<string | null>(null);
  const [signedInAs, setSignedInAs] = useState("");
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [server, setServer] = useState<ContactSettingsRecord | null>(null);
  const [editor, setEditor] = useState<ContactSettings>(() => copySettings(defaultContactSettings));
  const [busy, setBusy] = useState<"signin" | "save" | "publish" | "reload" | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [conflict, setConflict] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const hasChanges = Boolean(server && !settingsEqual(editor, server.draft));
  const hasUnpublishedDraft = Boolean(
    server && !settingsEqual(server.draft, server.published)
  );

  useEffect(() => {
    if (!hasChanges) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [hasChanges]);

  const showError = (caught: unknown) => {
    const requestError = caught instanceof RequestError ? caught : null;
    if (requestError?.status === 401) {
      setToken(null);
      setServer(null);
      setCredentials((current) => ({ ...current, password: "" }));
    }
    setConflict(requestError?.status === 409);
    setError(caught instanceof Error ? caught.message : "Something went wrong. Please try again.");
    setNotice("");
  };

  const signIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy("signin");
    setError("");
    setNotice("");
    try {
      const result = await request<SignInResponse>("/auth/signin", undefined, {
        method: "POST",
        body: JSON.stringify(credentials),
      });
      if (!result?.access_token) {
        throw new RequestError("Sign-in did not return an access token.", 0);
      }
      const latest = await getSettings(result.access_token);
      setToken(result.access_token);
      setSignedInAs(result.user?.email || credentials.email);
      setServer(latest);
      setEditor(copySettings(latest.draft));
      setCredentials({ email: "", password: "" });
      setConflict(false);
    } catch (caught) {
      showError(caught);
    } finally {
      setBusy(null);
    }
  };

  const loadLatest = async () => {
    if (!token) return;
    setBusy("reload");
    setError("");
    try {
      const latest = await getSettings(token);
      setServer(latest);
      if (!conflict) setEditor(copySettings(latest.draft));
      setConflict(false);
      setNotice(conflict ? "Version refreshed; your edits remain. Review them before saving, since saving will replace newer field values." : "The latest saved draft is loaded.");
    } catch (caught) {
      showError(caught);
    } finally {
      setBusy(null);
    }
  };

  const saveDraft = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!token || !server || !hasChanges || !formRef.current?.reportValidity()) return;
    setBusy("save");
    setError("");
    setNotice("");
    try {
      await request("/admin/contact-settings", token, {
        method: "PUT",
        body: JSON.stringify({ settings: editor, expectedVersion: server.draftVersion }),
      });
      const latest = await getSettings(token);
      setServer(latest);
      setEditor(copySettings(latest.draft));
      setConflict(false);
      setNotice("Draft saved. The public contact page has not changed yet.");
    } catch (caught) {
      showError(caught);
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (!token || !server || !formRef.current?.reportValidity()) return;
    setBusy("publish");
    setError("");
    setNotice("");
    try {
      let latest = server;
      if (hasChanges) {
        await request("/admin/contact-settings", token, {
          method: "PUT",
          body: JSON.stringify({ settings: editor, expectedVersion: server.draftVersion }),
        });
        latest = await getSettings(token);
      }
      await request("/admin/contact-settings/publish", token, {
        method: "POST",
        body: JSON.stringify({ expectedDraftVersion: latest.draftVersion }),
      });
      latest = await getSettings(token);
      setServer(latest);
      setEditor(copySettings(latest.draft));
      await reloadPublicSettings();
      setConflict(false);
      setNotice("Published. The public contact page is using these details.");
    } catch (caught) {
      showError(caught);
    } finally {
      setBusy(null);
    }
  };

  const updateField = (key: Exclude<keyof ContactSettings, "socials">, value: string) => {
    setEditor((current) => ({ ...current, [key]: value }));
    setNotice("");
  };

  const updateSocial = (id: string, key: "label" | "url" | "description" | "showInFooter", value: string | boolean) => {
    setEditor((current) => ({
      ...current,
      socials: current.socials.map((social) =>
        social.id === id ? { ...social, [key]: value } : social
      ),
    }));
    setNotice("");
  };

  const editorVisible = previewMode || Boolean(token && server);
  const previewLinks = contactSettingsToLinks(editor);

  return (
    <main className="contact-control-page">
      <div className="contact-control-shell">
        <header className="contact-control-header">
          <div className="contact-control-brand">
            <a className="contact-control-brand-logo" href="/home" aria-label="AlluraTech home">
              <span className="brand-symbol" aria-hidden="true"><img src="/alluratech-brand.png" alt="" /></span>
              <span className="brand-wordmark" aria-hidden="true"><img src="/alluratech-brand.png" alt="" /></span>
            </a>
            <span className="contact-control-header-rule" aria-hidden="true" />
            <strong>Contact page control</strong>
          </div>
          <div className="contact-control-header-right">
            <a href="/contact" target="_blank" rel="noopener noreferrer">View contact page <span aria-hidden="true">↗</span></a>
            {token && !previewMode && <button type="button" onClick={() => { setToken(null); setServer(null); setSignedInAs(""); setEditor(copySettings(defaultContactSettings)); setError(""); setNotice(""); }} className="contact-control-signout">Sign out</button>}
          </div>
        </header>

        {!editorVisible ? (
          <section className="contact-control-auth" aria-labelledby="contact-control-signin-title">
            <div className="contact-control-auth-copy">
              <h1 id="contact-control-signin-title">Manage your contact page.</h1>
              <p>Sign in to update the details people see when they get in touch with AlluraTech.</p>
            </div>
            <form onSubmit={signIn} className="contact-control-auth-form">
              <h2>Admin sign in</h2>
              <p>Use your AlluraTech admin account.</p>
              <label htmlFor="control-email">Email</label>
              <input id="control-email" type="email" autoComplete="username" required value={credentials.email} onChange={(event) => setCredentials((current) => ({ ...current, email: event.target.value }))} />
              <label htmlFor="control-password">Password</label>
              <input id="control-password" type="password" autoComplete="current-password" required value={credentials.password} onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))} />
              {error && <p className="contact-control-alert contact-control-alert--error" role="alert">{error}</p>}
              <button className="contact-control-button contact-control-button--primary" type="submit" disabled={busy === "signin"}>{busy === "signin" ? "Signing in…" : "Sign in"}</button>
            </form>
          </section>
        ) : (
          <>
            <div className="contact-control-intro">
              <div>
                <h1>Manage contact details</h1>
                <p>Edit the information visitors see, review the preview, then publish when it is ready.</p>
              </div>
              <div className="contact-control-meta">
                {previewMode ? <span>Local preview · changes cannot be saved</span> : <span>Signed in as {signedInAs}<br />Last published {dateLabel(server?.publishedAt || null)}</span>}
              </div>
            </div>

            {previewMode && <div className="contact-control-alert contact-control-alert--preview" role="status">Preview mode is for local review only. Sign in on the deployed site to save and publish.</div>}
            {error && <div className="contact-control-alert contact-control-alert--error" role="alert">{error}{conflict && <button type="button" onClick={loadLatest} disabled={Boolean(busy)}>Refresh version, keep edits</button>}</div>}
            {notice && <div className="contact-control-alert contact-control-alert--success" role="status">{notice}</div>}

            <div className="contact-control-columns">
              <section className="contact-control-editor" aria-labelledby="contact-control-editor-title">
                <div className="contact-control-panel-head">
                  <h2 id="contact-control-editor-title">Edit contact information</h2>
                  <p>These details appear on the public contact page after you publish.</p>
                </div>
                <form id="contact-control-form" ref={formRef} onSubmit={saveDraft}>
                  <div className="contact-control-fields">
                    <label htmlFor="control-location">Location</label>
                    <input id="control-location" required maxLength={120} value={editor.locationLabel} onChange={(event) => updateField("locationLabel", event.target.value)} placeholder="Helsinki, Finland" disabled={Boolean(busy)} />

                    <label htmlFor="control-public-email">Public email</label>
                    <input id="control-public-email" type="email" required maxLength={254} value={editor.email} onChange={(event) => updateField("email", event.target.value)} placeholder="hello@alluratech.fi" disabled={Boolean(busy)} />

                    <label htmlFor="control-headline">Headline</label>
                    <input id="control-headline" required maxLength={110} value={editor.heroTitle} onChange={(event) => updateField("heroTitle", event.target.value)} disabled={Boolean(busy)} />

                    <label htmlFor="control-description">Description</label>
                    <textarea id="control-description" required maxLength={400} rows={4} value={editor.heroDescription} onChange={(event) => updateField("heroDescription", event.target.value)} disabled={Boolean(busy)} />

                    <label htmlFor="control-channel">WhatsApp Channel URL</label>
                    <input id="control-channel" type="url" required value={editor.whatsappChannelUrl} onChange={(event) => updateField("whatsappChannelUrl", event.target.value)} placeholder="https://whatsapp.com/channel/…" disabled={Boolean(busy)} />

                    <label htmlFor="control-community">WhatsApp community URL</label>
                    <input id="control-community" type="url" required value={editor.whatsappCommunityUrl} onChange={(event) => updateField("whatsappCommunityUrl", event.target.value)} placeholder="https://chat.whatsapp.com/…" disabled={Boolean(busy)} />
                  </div>

                  <div className="contact-control-social-heading">
                    <h3>Social links</h3>
                    <p>Keep each active channel on a secure HTTPS URL.</p>
                  </div>
                  <div className="contact-control-socials">
                    {editor.socials.map((social) => (
                      <div className="contact-control-social" key={social.id}>
                        <div className="contact-control-social-main">
                          <span className="contact-control-social-mark" aria-hidden="true">{({ linkedin: "in", x: "X", telegram: "TG", tiktok: "♪", "founder-linkedin": "in" } as Record<string, string>)[social.id] || "↗"}</span>
                          <label htmlFor={`control-social-${social.id}`}>{social.label}</label>
                        </div>
                        <input id={`control-social-${social.id}`} type="url" pattern="https://.*" required value={social.url} onChange={(event) => updateSocial(social.id, "url", event.target.value)} placeholder="https://" disabled={Boolean(busy)} />
                        <details className="contact-control-social-details">
                          <summary>Edit display details</summary>
                          <div className="contact-control-social-extra">
                            <label htmlFor={`control-social-label-${social.id}`}>Display name</label>
                            <input id={`control-social-label-${social.id}`} value={social.label} onChange={(event) => updateSocial(social.id, "label", event.target.value)} maxLength={60} disabled={Boolean(busy)} />
                            <label htmlFor={`control-social-description-${social.id}`}>Short description</label>
                            <input id={`control-social-description-${social.id}`} value={social.description} onChange={(event) => updateSocial(social.id, "description", event.target.value)} maxLength={160} disabled={Boolean(busy)} />
                            <label className="contact-control-checkbox"><input type="checkbox" checked={social.showInFooter} onChange={(event) => updateSocial(social.id, "showInFooter", event.target.checked)} disabled={Boolean(busy)} /> Show in footer</label>
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                </form>
              </section>

              <section className="contact-control-preview-panel" aria-labelledby="contact-control-preview-title">
                <div className="contact-control-panel-head contact-control-preview-head">
                  <div><h2 id="contact-control-preview-title">Content preview</h2><p>Review the text and links before publishing.</p></div>
                  <a href="/contact" target="_blank" rel="noopener noreferrer">Open public page ↗</a>
                </div>
                <div className="contact-control-preview">
                  <div className="contact-control-preview-hero">
                    <div className="contact-control-preview-copy">
                      <h3>{editor.heroTitle || "Your headline"}</h3>
                      <p>{editor.heroDescription || "Your description will appear here."}</p>
                      <div className="contact-control-preview-ctas">
                        <span>Email AlluraTech <span aria-hidden="true">↗</span></span>
                        {editor.whatsappCommunityUrl && <span>Join WhatsApp community <span aria-hidden="true">↗</span></span>}
                      </div>
                    </div>
                    <div className="contact-control-preview-logo"><img src="/alluratech-brand.png" alt="" /></div>
                  </div>
                  <div className="contact-control-preview-lower">
                    <div className="contact-control-preview-heading"><h4>Find AlluraTech online</h4><p>Follow updates, meet the community, and explore what we are building.</p></div>
                    <div className="contact-control-preview-links">
                      {previewLinks.map((link) => <div key={link.name}><span>{link.label || link.name}</span><span aria-hidden="true">↗</span></div>)}
                    </div>
                    <div className="contact-control-preview-details"><span><small>Our base</small>{editor.locationLabel || "Your location"}</span><span><small>Email</small>{editor.email || "Your email"}</span></div>
                  </div>
                </div>
              </section>
            </div>

            <div className="contact-control-actionbar">
              <div className="contact-control-actionstatus"><span className={`contact-control-status-dot ${hasChanges ? "is-dirty" : hasUnpublishedDraft ? "is-draft" : "is-published"}`} aria-hidden="true" /><span>{previewMode ? "Local preview" : hasChanges ? "Unsaved changes" : hasUnpublishedDraft ? "Saved draft" : "Up to date"}</span><small>{previewMode ? "Try editing fields to preview their appearance." : hasChanges ? "Save a draft or publish your changes." : hasUnpublishedDraft ? "Your saved changes are awaiting publication." : "Your published page is current."}</small></div>
              <div className="contact-control-actions">
                {!previewMode && <button type="button" className="contact-control-button contact-control-button--secondary" onClick={() => { if (server) setEditor(copySettings(server.draft)); else setEditor(copySettings(defaultContactSettings)); setNotice(""); setError(""); }} disabled={Boolean(busy) || !hasChanges}>Discard changes</button>}
                <button type="submit" form="contact-control-form" className="contact-control-button contact-control-button--soft" disabled={Boolean(busy) || !hasChanges || previewMode}>{busy === "save" ? "Saving…" : "Save draft"}</button>
                <button type="button" onClick={publish} className="contact-control-button contact-control-button--primary" disabled={Boolean(busy) || previewMode || (!hasChanges && !hasUnpublishedDraft)}>{busy === "publish" ? "Publishing…" : "Publish changes"}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default ContactControlPage;
